import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FwTooltipDirective } from '@flywheel-io/vision';

import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Container } from '../../shared/models/container.model';
import { Group } from '../../shared/models/group.model';
import { Project, ProjectStats } from '../../shared/models/project.model';

@Component({
  host: {
    'test-id': 'project-info',
  },
  selector: 'project-info',
  templateUrl: './project-info.component.html',
  styleUrls: ['./project-info.component.scss'],
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwTooltipDirective,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
  ],
})
export class ProjectInfoComponent implements OnChanges {
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);
  private notifications = inject(NotificationService);
  private projectService = inject(ProjectsService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  @Input() container: Container;
  @Input() project: Project;
  @Input() projectStats: ProjectStats;
  @Input() group: Group;
  @Input('permissions') permissionSet = new Set<PermissionAction>();
  @Input() writable = false;

  @Output() afterSave = new EventEmitter<Container>();

  groupedProjects$ = this.projectService.getGroupedProjects([PermissionAction.ContainersCreateHierarchy]);
  permissions = {
    editMetadata: false,
  };
  projectId = new FormControl('');

  get projectHref(): string {
    return `/projects/${this.container?.project}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.permissionSet) {
      this.permissions = {
        editMetadata: this.writable && this.permissionSet.has(PermissionAction.ContainersModifyMetadata),
      };
    }
    if (changes.container && this.container?.project) {
      this.resetForm();
    }
  }

  groupLabel(group: Group): string {
    return `${group?.label} ${group?._id ? '(' + group._id + ')' : ''}`;
  }

  resetForm(): void {
    this.projectId.setValue(this.container.project);
  }

  saveChanges(): void {
    this.coreApi.containers.put(this.container._id, { project: this.projectId.value }).subscribe({
      next: () => {
        this.afterSave.emit(this.container);
        this.notifications.success('session.saveProject.success');
      },
      error: (error) => {
        this.logger.error(error);
        this.notifications.error('session.saveProject.error');
      },
    });
  }
}
